import { useState, useEffect, useCallback } from 'react';
import { fetchMeals } from './api';
import { useGoals }        from './useGoals';
import { useSubscription } from './useSubscription';
import { isLoggedIn, getUser, logout } from './auth';
import AuthScreen  from './components/AuthScreen';
import Dashboard   from './components/Dashboard';
import LogMeal     from './components/LogMeal';
import Stats       from './components/Stats';
import Settings    from './components/Settings';
import PricingModal from './components/PricingModal';

function getLast7Days() {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - i);
    return d.toISOString().split('T')[0];
  }).reverse();
}

export default function App() {
  const today = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  })();

  const [loggedIn,      setLoggedIn]     = useState(isLoggedIn());
  const [user,          setUser]         = useState(getUser());
  const [tab,           setTab]          = useState('home');
  const [date,          setDate]         = useState(today);
  const [meals,         setMeals]        = useState([]);
  const [weekData,      setWeekData]     = useState([]);
  const [editMeal,      setEditMeal]     = useState(null);
  const [showPricing,   setShowPricing]  = useState(false);
  const [pricingReason, setPricingReason] = useState('');

  const { goals, updateGoals } = useGoals();
  const { isPro, interval, periodEnd, refresh } = useSubscription();

  // ── Handle Stripe redirect ──────────────────────────────────
  useEffect(() => {
  const params = new URLSearchParams(window.location.search);

  if (params.get('upgrade') === 'success') {
    window.history.replaceState({}, '', window.location.pathname);
    refresh();
    // Small delay to let webhook process before refreshing
    setTimeout(() => refresh(), 3000);
  }

  if (params.get('upgrade') === 'cancelled') {
    window.history.replaceState({}, '', window.location.pathname);
  }

  if (params.get('billing') === 'returned') {
    // Clean up URL immediately
    window.history.replaceState({}, '', window.location.pathname);
    // Refresh subscription status in case they cancelled
    refresh();
  }
}, [refresh]);

  // ── Load meals ──────────────────────────────────────────────
  const loadMeals = useCallback(async () => {
    if (!loggedIn) return;
    try {
      const data = await fetchMeals(date);
      setMeals(data);
    } catch (e) { console.error(e); }
  }, [date, loggedIn]);

  // ── Load 7-day data for stats ───────────────────────────────
  const loadWeekData = useCallback(async () => {
    if (!loggedIn) return;
    try {
      const dates   = getLast7Days();
      const results = await Promise.all(dates.map(d => fetchMeals(d)));
      setWeekData(dates.map((d, i) => {
        const t = results[i].reduce((acc, m) => ({
          calories: acc.calories + m.calories,
          protein:  acc.protein  + m.protein,
          carbs:    acc.carbs    + m.carbs,
          fat:      acc.fat      + m.fat,
        }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
        return {
          day:      d.slice(5),
          date:     d,
          calories: Math.round(t.calories),
          protein:  Math.round(t.protein),
          carbs:    Math.round(t.carbs),
          fat:      Math.round(t.fat),
        };
      }));
    } catch (e) { console.error(e); }
  }, [loggedIn]);

  useEffect(() => { loadMeals(); },                         [loadMeals]);
  useEffect(() => { if (tab === 'stats') loadWeekData(); }, [tab, loadWeekData]);

  // ── 401 logout listener ─────────────────────────────────────
  useEffect(() => {
    const handler = () => {
      setLoggedIn(false);
      setUser(null);
      setMeals([]);
      setWeekData([]);
      setTab('home');
    };
    window.addEventListener('auth:logout', handler);
    return () => window.removeEventListener('auth:logout', handler);
  }, []);

  // ── Auth handlers ───────────────────────────────────────────
  function handleAuth(userData) {
    setUser(userData);
    setLoggedIn(true);
  }

  function handleLogout() {
    logout();
    setLoggedIn(false);
    setUser(null);
    setMeals([]);
    setWeekData([]);
    setTab('home');
  }

  function handleUpgrade(reason = '') {
    setPricingReason(reason);
    setShowPricing(true);
  }

  // ── Auth gate ───────────────────────────────────────────────
  if (!loggedIn) return <AuthScreen onAuth={handleAuth} />;

  const tabs = [
    { id: 'home',     icon: '🏠', label: 'Home'     },
    { id: 'log',      icon: '➕', label: 'Log'      },
    { id: 'stats',    icon: '📊', label: 'Stats'    },
    { id: 'settings', icon: '⚙️', label: 'Settings' },
  ];

  return (
    <div style={{
      maxWidth: 480, margin: '0 auto',
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      minHeight: '100vh', display: 'flex',
      flexDirection: 'column', color: '#fff',
      position: 'relative',
    }}>

      {/* Background blobs */}
      <div style={{
        position: 'fixed', top: -100, right: -100,
        width: 300, height: 300, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(82,130,255,0.25) 0%, transparent 70%)',
        pointerEvents: 'none', zIndex: 0,
      }} />
      <div style={{
        position: 'fixed', bottom: 100, left: -80,
        width: 250, height: 250, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(168,85,247,0.18) 0%, transparent 70%)',
        pointerEvents: 'none', zIndex: 0,
      }} />

      {/* ── Header ── */}
      <div style={{
        padding: '14px 20px',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 10,
        background: 'rgba(15,20,40,0.85)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
      }}>
        <div>
          <div style={{
            fontSize: 18, fontWeight: 700, letterSpacing: 0.5,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <span style={{
              background: 'linear-gradient(135deg, #fff 0%, #a0c4ff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              🥗 Diet Tracker
            </span>
            {isPro && (
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '2px 7px',
                borderRadius: 999,
                background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                color: '#1a1a2e',
              }}>
                PRO
              </span>
            )}
          </div>
          <div style={{
            fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 2,
          }}>
            Hi, {user?.name?.split(' ')[0]} 👋
          </div>
        </div>
        <button onClick={handleLogout} style={{
          fontSize: 12, color: 'rgba(255,100,100,0.9)',
          background: 'rgba(255,100,100,0.1)',
          border: '1px solid rgba(255,100,100,0.3)',
          borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
        }}>
          Logout
        </button>
      </div>

      {/* ── Content ── */}
      <div style={{
        flex: 1, padding: 16, paddingBottom: 90,
        overflowY: 'auto', position: 'relative', zIndex: 1,
      }}>
        {tab === 'home' && (
          <Dashboard
            meals={meals}
            goals={goals}
            date={date}
            onDateChange={setDate}
            onEditMeal={(meal) => { setEditMeal(meal); setTab('log'); }}
          />
        )}
        {tab === 'log' && (
          <LogMeal
            onMealLogged={() => { loadMeals(); setTab('home'); }}
            editMeal={editMeal}
            onCancelEdit={() => { setEditMeal(null); setTab('home'); }}
            isPro={isPro}
            onUpgrade={handleUpgrade}
          />
        )}
        {tab === 'stats' && (
          <Stats weekData={weekData} goals={goals} />
        )}
        {tab === 'settings' && (
  	<Settings
    		goals={goals}
    		onSave={updateGoals}
    		isPro={isPro}
    		interval={interval}
    		periodEnd={periodEnd}
    		onUpgrade={handleUpgrade}
  	/>
	)}
      </div>

      {/* ── Bottom nav ── */}
      <div style={{
        position: 'fixed', bottom: 0,
        left: '50%', transform: 'translateX(-50%)',
        width: '100%', maxWidth: 480, zIndex: 10,
        background: 'rgba(15,20,40,0.92)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        display: 'flex',
      }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            flex: 1, padding: '10px 0',
            background: 'none', border: 'none', cursor: 'pointer',
            borderTop: tab === t.id
              ? '2px solid #5282ff'
              : '2px solid transparent',
            color: tab === t.id
              ? '#5282ff'
              : 'rgba(255,255,255,0.45)',
            transition: 'all 0.2s',
          }}>
            <div style={{ fontSize: 20 }}>{t.icon}</div>
            <div style={{
              fontSize: 11, marginTop: 2,
              fontWeight: tab === t.id ? 600 : 400,
            }}>
              {t.label}
            </div>
          </button>
        ))}
      </div>

      {/* ── Pricing modal ── */}
      {showPricing && (
        <PricingModal
          reason={pricingReason}
          onClose={() => setShowPricing(false)}
        />
      )}
    </div>
  );
}